import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { creditMovementService } from "../services/creditMovementService";
import { creditMovementDisplayId } from "./CreditMovementCommonStore";
import { CreditMovement } from "../../../api/graphql";
import { creditPurchaseDisplayId } from "../../creditPurchase/store/CreditPurchaseCommonStore";
import { CreditMovementType } from "../../../api/graphql";
import { creditPurchaseService } from "../../creditPurchase/services/creditPurchaseService";
import { InitializeParams } from "../../../store/types";

export class CreditMovementViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = creditMovementService;
  creditMovementInstance: CreditMovement | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  typeValue: CreditMovementType = "" as CreditMovementType;
  deltaValue: number = 0;
  balanceAfterValue: number = 0;
  movementAtValue: Date | null = null;
  detailValue: string = "";
  editionKeyValue: string = "";
  editionNameValue: string = "";
  purchaseService = creditPurchaseService;
  purchaseSearchValue: string = "";
  purchaseIdValue: string = "";
  purchaseSelectedValue: { displayId: string; id: string } | null = null;

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadCreditMovementById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.creditMovementOne({ id });
    this.creditMovementInstance = result.creditMovementOne;
    if (this.creditMovementInstance) {
      this.loadValues(this.creditMovementInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.creditMovementInstance) {
      return creditMovementDisplayId(
        this.creditMovementInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.CreditMovementSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "purchase") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.creditPurchaseOne({
          id: params.returnData.resultId,
        });
        const creditPurchase = result.creditPurchaseOne;
        this.purchaseSelectedValue = creditPurchase;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  getTypeOptions() {
    return this.langStore.CreditMovementTypeOptions;
  }

  get purchaseValue() {
    if (this.purchaseSelectedValue) {
      return {
        id: this.purchaseSelectedValue.id,
        displayId: creditPurchaseDisplayId(
          this.purchaseSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.creditMovementInstance = null;
    this.typeValue = "" as CreditMovementType;
    this.deltaValue = 0;
    this.balanceAfterValue = 0;
    this.movementAtValue = null;
    this.detailValue = "";
    this.editionKeyValue = "";
    this.editionNameValue = "";
    this.purchaseSearchValue = "";
    this.purchaseIdValue = "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelCreditMovement() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editCreditMovement() {
    const id = this.creditMovementInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/creditMovement/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printCreditMovement(handlePrint: () => void): void {
    handlePrint();
  }

  *closeCreditMovement(): any {
    yield this.close();
  }

  loadValues(instance: CreditMovement) {
    this.idValue = instance.id;
    this.typeValue = instance.type;
    this.deltaValue = instance.delta;
    this.balanceAfterValue = instance.balanceAfter;
    this.movementAtValue = new Date(instance.movementAt);
    this.detailValue = instance.detail;
    this.editionKeyValue = instance.editionKey || "";
    this.editionNameValue = instance.editionName || "";
    this.purchaseSelectedValue = instance.purchase ? instance.purchase : null;
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadCreditMovementById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
